import { Icon24ArrowLeftOutline } from '@vkontakte/icons';
import { IconButton } from '@vkontakte/vkui';
import React, { useMemo } from 'react';
import { FastCheckoutSidebar } from 'reactApp/components/VkBuyModalWithSidebar/FastCheckoutSidebar/FastCheckoutSidebar';
import { TrialPromoSidebar } from 'reactApp/components/VkBuyModalWithSidebar/TrialPromoSidebar/TrialPromoSidebar';
import styles from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.css';
import { type Options, EPaymentModalType } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.types';
import type { Product, Tariff } from 'reactApp/types/Billing';
import { PaymentFooter } from 'reactApp/ui/PaymentFooter/PaymentFooter';
import { getPeriodName } from 'reactApp/utils/Period';

interface HookProps {
    type: EPaymentModalType;
    product: Product;
    changeProduct: (id: string) => void;
    onClickTariffs: () => void;
    showBackButton?: boolean;
    onBackClick?: () => void;
    tariff: Tariff;
    options?: Options;
    featuresList?: string[];
    isFrame?: boolean;
    title?: string;
    hideOtherTariffs?: boolean;
    hideSwitchSidebarBlock?: boolean;
}

export const useContent = ({
    type,
    product,
    changeProduct,
    onClickTariffs,
    showBackButton,
    onBackClick = () => {},
    tariff,
    options,
    featuresList,
    isFrame,
    title,
    hideOtherTariffs = false,
    hideSwitchSidebarBlock = false,
}: HookProps) => {
    const sidebar = useMemo(() => {
        if (type === EPaymentModalType.trial || type === EPaymentModalType.attachesTrial) {
            return <TrialPromoSidebar product={product} options={options} />;
        }

        if (type === EPaymentModalType.fastcheckout) {
            return (
                <FastCheckoutSidebar
                    product={product}
                    onProductChange={changeProduct}
                    onClickTariffs={onClickTariffs}
                    tariff={tariff}
                    featuresList={featuresList}
                    isBuy={isFrame}
                    hideOtherTariffs={hideOtherTariffs}
                    hideSwitchSidebarBlock={hideSwitchSidebarBlock}
                />
            );
        }
    }, [type, product, changeProduct, onClickTariffs, tariff, isFrame, hideOtherTariffs]);

    const headerText = useMemo(() => {
        if (title) {
            return title;
        }

        const trialPeriodName = product.hasTrial && product.trialPeriod && getPeriodName(product.trialPeriod, false, true);

        if (product?.isFamily && product?.isOnlyFeatures) {
            return trialPeriodName ? `Подключить семейные функции на ${trialPeriodName} бесплатно` : 'Подключить семейные функции';
        }

        if (product?.isFamily) {
            return trialPeriodName ? `Семейная подписка Mail Space на ${trialPeriodName} бесплатно` : 'Оплата семейной подписки Mail Space';
        }

        if (type === EPaymentModalType.fastcheckout) {
            return trialPeriodName ? `Подписка Mail Space на ${trialPeriodName} бесплатно` : 'Оплата подписки Mail Space';
        }
    }, [type, title, product]);

    const header = useMemo(() => {
        if (headerText) {
            return (
                <div className={styles.headerContainer}>
                    {showBackButton && (
                        <IconButton hasHover={false} onClick={onBackClick} aria-label="Назад">
                            <Icon24ArrowLeftOutline />
                        </IconButton>
                    )}
                    <div className={styles.header}>{headerText}</div>
                </div>
            );
        }
    }, [headerText]);

    const footer = useMemo(() => {
        if (type === EPaymentModalType.fastcheckout) {
            return <PaymentFooter productId={product?.id} />;
        }
    }, [product?.id, type]);

    return {
        sidebar,
        header,
        footer,
    };
};
